import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
  ReCaptchaV3Provider,
  type AppCheck,
} from 'firebase/app-check';
import { connectAuthEmulator, getAuth, type Auth } from 'firebase/auth';
import { connectFirestoreEmulator, getFirestore, type Firestore } from 'firebase/firestore';
import { connectStorageEmulator, getStorage, type FirebaseStorage } from 'firebase/storage';
import { connectFunctionsEmulator, getFunctions, type Functions } from 'firebase/functions';
import { getMessaging, isSupported, type Messaging } from 'firebase/messaging';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

function assertConfig(): void {
  const missing = Object.entries(firebaseConfig)
    .filter(([, v]) => !v)
    .map(([k]) => k);
  if (missing.length > 0) {
    throw new Error(
      `Firebase config incompleta. Faltam: ${missing.join(', ')}. Verifique o .env local.`
    );
  }
}

assertConfig();

export const app: FirebaseApp = initializeApp(firebaseConfig);

// App Check — debug token em DEV (Vite remove no build). Token fixo do .env.local vale em
// qualquer janela/porta (inclusive anônima); sem ele o SDK gera um por navegador.
if (import.meta.env.DEV) {
  // @ts-expect-error — propriedade global injetada pelo Firebase em DEV
  self.FIREBASE_APPCHECK_DEBUG_TOKEN = import.meta.env.VITE_APPCHECK_DEBUG_TOKEN || true;
}

const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
let appCheck: AppCheck | undefined;
if (recaptchaSiteKey) {
  appCheck = initializeAppCheck(app, {
    // Chave nova do Google Cloud = Enterprise (sem chave secreta); a da GAIA = v3 (clinicas/README.md, App Check).
    provider: import.meta.env.VITE_RECAPTCHA_ENTERPRISE === '1'
      ? new ReCaptchaEnterpriseProvider(recaptchaSiteKey)
      : new ReCaptchaV3Provider(recaptchaSiteKey),
    isTokenAutoRefreshEnabled: true,
  });
} else if (!import.meta.env.DEV) {
  console.warn('[App Check] VITE_RECAPTCHA_SITE_KEY não configurada em produção');
}

export { appCheck };
export const auth: Auth = getAuth(app);
export const db: Firestore = getFirestore(app);
export const storage: FirebaseStorage = getStorage(app);
export const functions: Functions = getFunctions(app, 'southamerica-east1');

// Emulador local (`npm run dev:emulador`, .env.emulador). Fora desse modo nada aqui roda.
if (import.meta.env.VITE_USE_EMULATORS === '1') {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  connectStorageEmulator(storage, '127.0.0.1', 9199);
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);
}

// FCM messaging — só inicializa se browser suportar (iOS pré-16.4 não suporta)
let _messaging: Messaging | null = null;
isSupported()
  .then((supported) => {
    if (supported) {
      _messaging = getMessaging(app);
    }
  })
  .catch(() => {
    /* messaging unavailable — graceful degrade */
  });

export function getMessagingIfSupported(): Messaging | null {
  return _messaging;
}

export const fcmVapidKey: string | undefined = import.meta.env.VITE_FCM_VAPID_KEY;
